import {
    Component,
    AfterViewChecked,
    OnChanges,
    OnDestroy,
    ViewChild,
    ElementRef,
    Input,
    SimpleChanges
} from '@angular/core';
import { Chart, ChartConfiguration, registerables } from 'chart.js';

// Register all Chart.js components once
Chart.register(...registerables);

/**
 * Reusable canvas-based chart wrapper.
 *
 * Usage:
 *   <app-chart-canvas [canvasId]="'myChart'" [data]="chartData"></app-chart-canvas>
 *
 * The `data` input mirrors a Chart.js `ChartData` object and may also carry a
 * top-level `type` property (e.g. 'bar', 'line', 'doughnut') and an `options`
 * object that will be merged into the chart configuration.
 */
@Component({
    selector: 'app-chart-canvas',
    template: '<canvas #canvas [id]="canvasId"></canvas>'
})
export class ChartCanvasComponent implements AfterViewChecked, OnChanges, OnDestroy
{
    @Input() data: any;
    @Input() canvasId: string;
    @Input() type: string;

    @ViewChild('canvas', { static: false }) canvas: ElementRef<HTMLCanvasElement>;

    private chart: Chart | null = null;
    private rendered = false;

    ngAfterViewChecked(): void
    {
        if (!this.rendered) { this.render(); }
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        if (changes['data'] && !changes['data'].firstChange)
        {
            this.rendered = false;
            this.destroy();
            this.render();
        }
    }

    ngOnDestroy(): void { this.destroy(); }

    private render(): void
    {
        if (!this.data || !this.canvas) { return; }

        const ctx = this.canvas.nativeElement.getContext('2d');
        if (!ctx) { return; }

        const config: ChartConfiguration = {
            type: this.type || this.data.type || 'bar',
            data: this.data,
            options: {
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                    legend: {
                        display: this.data.options?.plugins?.legend?.display ?? false
                    }
                },
                ...this.data.options
            }
        };

        try
        {
            this.chart = new Chart(ctx, config);
            this.rendered = true;
        }
        catch (err)
        {
            console.error(`ChartCanvasComponent: failed to render chart "${this.canvasId}"`, err);
        }
    }

    private destroy(): void
    {
        if (this.chart)
        {
            try { this.chart.destroy(); } catch { /* ignore */ }
            this.chart = null;
        }
    }
}
